/**
 * `?next=` - where to land after signing in (M2.9).
 *
 * The value arrives from a URL, which means it arrives from anyone who can get a
 * patient to click a link. An open redirect on a sign-in page is the classic way to
 * make a phishing page look like it came from the clinic: the patient signs in on the
 * real site, and the real site then sends them somewhere else entirely.
 *
 * So the rule is narrow and has no exceptions: the target must be a path on this app.
 * Anything else - an absolute URL, a protocol-relative `//evil.example`, a backslash
 * that some parsers read as a slash, a `javascript:` URL - resolves to the fallback.
 */

/** Where a patient goes when there is no usable `next`. */
export const DEFAULT_NEXT_PATH = '/account';

export function safeNextPath(raw: unknown, fallback: string = DEFAULT_NEXT_PATH): string {
  if (typeof raw !== 'string') return fallback;

  const value = raw.trim();
  if (value === '') return fallback;

  // Must be a rooted path, and only one slash: `//host` is protocol-relative.
  if (!value.startsWith('/') || value.startsWith('//')) return fallback;
  // `\` is a path separator to some URL parsers, so `/\evil.example` is an escape.
  if (value.includes('\\')) return fallback;
  // A control character anywhere is a smuggling attempt, never a real route.
  // eslint-disable-next-line no-control-regex
  if (/[ -]/.test(value)) return fallback;

  return value;
}

/**
 * `/login?next=<here>` for the route the patient was trying to reach.
 *
 * The search string is carried too, because a portal deep link ("this appointment")
 * is useless without it - and it is still only ever a path on this app.
 */
export function loginPathFor(pathname: string, search: string = ''): string {
  const target = safeNextPath(`${pathname}${search}`, DEFAULT_NEXT_PATH);
  return `/login?next=${encodeURIComponent(target)}`;
}
